import { Injectable } from "@angular/core";
import { v4 as uuid } from "uuid";
import {
  CategoryLookup,
  CursorLocation,
  CursorType,
  Expression,
  ExpressionLocation,
  Lineage,
  OperandType,
  OperatorType,
  PresentationalNode,
  PresentationalNodeType,
} from "./category-builder.models";

@Injectable({ providedIn: "root" })
export class ExpressionService {
  constructor() {}

  addExpressionChild(
    expression: Expression,
    location: ExpressionLocation,
    newChild: Expression,
  ): Expression {
    // When adding an Expression there are some special cases
    let child;
    switch (newChild.type) {
      case OperandType.PHRASE_GROUP:
        child = this.phraseGroupToGroupOfPhrases(newChild);
        break;
      default:
        child = newChild;
        break;
    }

    if (expression.id === location.parentID) {
      const sameType = expression.type === newChild.type;
      const typeIsAndOr =
        newChild.type === OperatorType.AND || newChild.type === OperatorType.OR;
      const hasNoChildren = !expression.children?.length;
      if (sameType && typeIsAndOr && hasNoChildren) {
        return {
          ...expression,
          children: child.children,
        };
      }

      const children = expression.children ? [...expression.children] : [];
      children.splice(location.parentIndex, 0, { ...child });
      return {
        ...expression,
        children,
      };
    }
    if (expression.children) {
      return {
        ...expression,
        children: expression.children?.map((c) =>
          this.addExpressionChild(c, location, child),
        ),
      };
    }

    return expression;
  }

  removeExpressionChild(
    expression: Expression,
    location: ExpressionLocation,
  ): Expression {
    if (expression.id === location.parentID) {
      const children = expression.children ? [...expression.children] : [];
      children.splice(location.parentIndex, 1);
      return {
        ...expression,
        children,
      };
    }

    if (expression.children) {
      return {
        ...expression,
        children: expression.children?.map((child) =>
          this.removeExpressionChild(child, location),
        ),
      };
    }

    return expression;
  }

  _removeUnnecessaryGroups(expression: Expression) {
    if (expression.children) {
      expression.children.forEach((child) =>
        this._removeUnnecessaryGroups(child),
      );
    }

    if (expression.children) {
      expression.children = expression.children.map((child) => {
        const operatorTypes: string[] = Object.values(OperatorType);
        const childIsOperator = operatorTypes.includes(child.type);
        const shouldRemove = childIsOperator && child.children?.length === 1;
        let newChild = child;
        if (shouldRemove) {
          newChild = child.children[0];
        }
        return newChild;
      });
    }
  }

  negateExpressionChild(
    expression: Expression,
    negate: boolean,
    location: ExpressionLocation,
  ): Expression {
    if (expression.id === location.parentID) {
      const children = expression.children
        ? expression.children.map((c, i) => {
            if (i === location.parentIndex) {
              return {
                ...c,
                negate,
              };
            }
            return c;
          })
        : [];
      return {
        ...expression,
        children,
      };
    }

    if (expression.children) {
      return {
        ...expression,
        children: expression.children?.map((child) =>
          this.negateExpressionChild(child, negate, location),
        ),
      };
    }

    return expression;
  }

  moveExpressionChild(
    expression: Expression,
    initialLocation: ExpressionLocation,
    finalLocation: ExpressionLocation,
    movingChild: Expression,
  ) {
    let newRoot = expression;
    if (initialLocation.parentID !== finalLocation.parentID) {
      // We can add and remove in any order without any issue
      newRoot = this.addExpressionChild(newRoot, finalLocation, movingChild);
      newRoot = this.removeExpressionChild(newRoot, initialLocation);
    } else if (initialLocation.parentIndex > finalLocation.parentIndex) {
      // We must delete before adding
      newRoot = this.removeExpressionChild(newRoot, initialLocation);
      newRoot = this.addExpressionChild(newRoot, finalLocation, movingChild);
    } else if (initialLocation.parentIndex < finalLocation.parentIndex) {
      // We must add before deleting
      newRoot = this.addExpressionChild(newRoot, finalLocation, movingChild);
      newRoot = this.removeExpressionChild(newRoot, initialLocation);
    }
    // this._removeUnnecessaryGroups(newRoot)
    return newRoot;
  }

  changeOperatorAtIndex(
    expression: Expression,
    expressionID: string,
    newType: OperatorType,
    index: number = -1,
    subGroupID: string = uuid(),
  ): { updated: Expression; newOperator?: Expression } {
    if (expression.id === expressionID) {
      if (expression.children.length === 2 || index < 0) {
        // If there are two children or if no index was specified
        // we can simply switch the operator
        const updated = {
          ...expression,
          type: newType,
        };
        return { updated };
      } else if (index > 0 && expression.children.length > 2) {
        // Otherwise we need to add a new child operator
        const newChildren = [...expression.children];
        const newOperator = {
          type: newType,
          id: subGroupID,
          children: newChildren.splice(index - 1, 2),
        };
        newChildren.splice(index - 1, 0, newOperator);
        const updated = {
          ...expression,
          children: newChildren,
        };
        return {
          updated,
          newOperator,
        };
      }
    }

    if (expression.children) {
      let newO;
      const up = {
        ...expression,
        children: expression.children?.map((child) => {
          const { updated, newOperator } = this.changeOperatorAtIndex(
            child,
            expressionID,
            newType,
            index,
            subGroupID,
          );

          // This mapping assumes there will only ever be one
          newO = newO || newOperator;
          return updated;
        }),
      };

      return {
        updated: up,
        newOperator: newO,
      };
    }

    return { updated: expression };
  }

  removeSubGroup(expression: Expression, expressionID: string): Expression {
    const subGroup = expression.children?.find(
      (child) => child.id === expressionID,
    );
    const subGroupIndex = expression.children?.findIndex(
      (child) => child.id === expressionID,
    );
    const subGroupInExpression = !!subGroup;

    if (subGroupInExpression) {
      const newChildren = [...expression.children];
      newChildren?.splice(subGroupIndex, 1, ...subGroup.children);
      return {
        ...expression,
        children: newChildren,
      };
    } else if (expression.children) {
      expression = {
        ...expression,
        children: expression.children.map((child) =>
          this.removeSubGroup(child, expressionID),
        ),
      };
    }

    return expression;
  }

  setGroupName(expression: Expression, expressionID: string, label: string) {
    if (expression.id === expressionID) {
      return {
        ...expression,
        label,
      };
    }

    if (expression.children) {
      return {
        ...expression,
        children: expression.children?.map((child) =>
          this.setGroupName(child, expressionID, label),
        ),
      };
    }

    return expression;
  }

  copyExpression(expression: Expression): Expression {
    const output = { ...expression };

    if (expression.id) {
      output.id = uuid();
    }

    if (expression.children) {
      output.children = expression.children.map((child) =>
        this.copyExpression(child),
      );
    }

    return output;
  }

  newOperator(type: OperatorType): Expression {
    return {
      type,
      id: uuid(),
      children: [],
    };
  }

  toggleGroupExpansion(
    expand: boolean,
    expression: Expression,
    expandedGroups: { [groupId: string]: boolean } = {},
  ) {
    if (this.isOperator(expression)) {
      expandedGroups[expression.id] = expand;
      expression.children.forEach((child) =>
        this.toggleGroupExpansion(expand, child, expandedGroups),
      );
    }
    return expandedGroups;
  }

  substituteCategoryReferences(
    expression: Expression,
    categories: CategoryLookup,
  ) {
    let substituted;
    switch (expression.type) {
      case OperandType.CATEGORY_REFERENCE:
        substituted = {
          ...categories[expression.value], // The referenced category
          negate: expression.negate,
        };
        break;
      default:
        substituted = { ...expression };
        break;
    }

    if (substituted.children) {
      substituted.children = substituted.children?.map((e) =>
        this.substituteCategoryReferences(e, categories),
      );
    }

    return substituted;
  }

  present(
    expression: Expression,
    expandedGroups: { [groupId: string]: boolean },
    cursorLocation: CursorLocation,
    parent: Expression = null,
    parentage: Lineage[] = [],
    indexInParent: number = 0,
    firstPresentedChild: boolean = true,
    lastPresentedChild: boolean = false,
  ): PresentationalNode[] {
    const expanded =
      !parent || (expandedGroups && expandedGroups[expression.id]);
    const node = new PresentationalNode({
      type: PresentationalNodeType.Expression,
      expression,
      parentage,
      parentOperator: parent?.type as OperatorType,
      expandable: expression.children?.length > 0,
      expanded: expanded,
      location: {
        parentID: parent?.id,
        parentIndex: indexInParent,
      },
      firstPresentedChild,
      lastPresentedChild,
      draggable: !expanded,
    });

    const newParentage = parentage.map((l, i) => {
      if (i == parentage.length - 1) {
        return {
          ...l,
          visible: !lastPresentedChild,
          tag: false,
          head: false,
          tail: false,
        };
      }
      return { ...l };
    });

    const cursorIsHead = cursorLocation?.parentIndex === 0;
    const cursorIsTail =
      cursorLocation?.parentIndex === expression?.children?.length;
    const cursorNode = new PresentationalNode({
      type: PresentationalNodeType.Cursor,
      parentage: [
        ...newParentage,
        {
          id: expression.id,
          visible: true,
          head: cursorIsHead,
          tail: cursorIsTail,
          tag: true,
        },
      ],
      parentOperator: expression.type as OperatorType,
      expandable: false,
      expanded: false,
      location: cursorLocation,
      firstPresentedChild: cursorIsHead,
      lastPresentedChild: cursorIsTail,
      draggable: false,
    });

    // If the node is expanded, we add all the nodes to represent its children
    let children = node.expanded
      ? expression.children?.reduce((list, child, index) => {
          const isCursorHere =
            cursorLocation?.parentID === expression.id &&
            cursorLocation?.parentIndex === index;

          const isCursorAfter =
            cursorLocation?.parentID === expression.id &&
            cursorLocation?.parentIndex === index + 1;

          const head = index === 0 && !isCursorHere;
          const tail =
            index === expression?.children?.length - 1 && !isCursorAfter;

          let nodes = this.present(
            child,
            expandedGroups,
            cursorLocation,
            expression,
            [
              ...newParentage,
              { id: expression.id, visible: true, head, tail, tag: true },
            ],
            index,
            head,
            tail,
          );

          if (isCursorHere && cursorLocation.type === CursorType.Edit) {
            // Remove the first node as it is the one being edited
            nodes = nodes.slice(1);

            // Add the expression being edited to the node
            cursorNode.expression = child;
          }

          return [...list, ...(isCursorHere ? [cursorNode] : []), ...nodes];
        }, [])
      : [];

    // Special case for the cursor: it's an Insert after the last element
    if (
      cursorLocation?.parentID === expression.id &&
      cursorLocation?.parentIndex >= expression.children?.length &&
      cursorLocation.type === CursorType.Insert
    ) {
      return [node, ...children, cursorNode];
    }

    return [node, ...children];
  }

  phraseGroupToGroupOfPhrases(phraseGroup: Expression) {
    const group = this.newOperator(OperatorType.OR);

    // Create the phrase children
    group.children = phraseGroup.phrases.map((phraseValue) => {
      const phrase: Expression = {
        type: OperandType.PHRASE,
        value: phraseValue,
      };
      return phrase;
    });

    // Sort them alphabetically for the UX feelz
    group.children.sort((a, b) => {
      if (a.value < b.value) {
        return -1;
      }
      if (a.value > b.value) {
        return 1;
      }
      return 0;
    });

    // Set the group label
    group.label = phraseGroup.value;

    // Negate the group properly
    group.negate = phraseGroup.negate;

    return group;
  }

  getToggledOperator(operatorType: OperatorType) {
    let changedOperator;
    switch (operatorType) {
      case OperatorType.AND:
        changedOperator = OperatorType.OR;
        break;
      case OperatorType.OR:
        changedOperator = OperatorType.AND;
        break;
      default:
        // Unchanged
        changedOperator = operatorType;
    }
    return changedOperator;
  }

  isOperator(expression: Expression) {
    return Object.values(OperatorType).some((v) => v === expression.type);
  }

  getToggledOperatorOfExpression(
    parentID: string,
    children: Expression[],
  ): OperatorType {
    for (let expression of children) {
      if (
        expression?.id === parentID &&
        (expression.type === OperatorType.AND ||
          expression.type === OperatorType.OR)
      )
        return this.getToggledOperator(expression.type);
      if (expression.children) {
        const toggledOperator = this.getToggledOperatorOfExpression(
          parentID,
          expression.children,
        );
        if (toggledOperator) return toggledOperator;
      }
    }
  }

  getLineage(expression: Expression, location: ExpressionLocation): string[] {
    if (expression.id === location.parentID) {
      return [expression.id];
    } else {
      if (expression.children) {
        for (const c of expression.children) {
          const lineage = this.getLineage(c, location);
          if (lineage.length) {
            return [expression.id, ...lineage];
          }
        }
      }
    }
    return [];
  }
}
